import { Box, Button, Divider, Drawer, IconButton, Paper, Stack, Typography } from '@mui/material';
import React from 'react';
import { FormProvider } from 'react-hook-form';
import { StickyToolbar } from '../StickyToolbar/StickyToolbar';
import { AddCreditDrawerContent } from './add-credit-drawer-components/AddCreditDrawerContent';
import type { AddCreditForm } from './add-credit-form/add-credit-form-schema';
import { useAddCreditForm } from './add-credit-form/use-add-credit-form';
import CloseIcon from '@mui/icons-material/Close';

type AddCreditDrawerProps = {
  open: boolean;
  onClose: () => void;
  onAddCredit?: (data: AddCreditForm) => void;
  defaultValues?: Partial<AddCreditForm>;
  title?: string;
  subtitle?: string;
  submitLabel?: string;
};

export const AddCreditDrawer: React.FC<AddCreditDrawerProps> = ({
  open,
  onClose,
  onAddCredit,
  defaultValues,
  title = 'Add credit',
  subtitle = 'Configure prepaid balance for future charges',
  submitLabel = 'Add credit',
}) => {
  const formMethods = useAddCreditForm(defaultValues);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
          },
        },
        backdrop: {
          invisible: true,
        },
      }}
    >
      <FormProvider {...formMethods}>
        <Paper
          elevation={1}
          sx={{
            margin: 2,
            height: '100%',
            minWidth: 576,
            maxWidth: 576,
            borderRadius: 2,
            border: '1px solid #E7EEF3',
            backgroundColor: '#fff',
            boxShadow: '0px 8px 24px 0px #00000014',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Stack
            direction="row"
            sx={{ justifyContent: 'space-between', alignItems: 'flex-start', padding: 2 }}
          >
            <Box>
              <Typography variant="body1">{title}</Typography>
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            </Box>
            <IconButton onClick={onClose} data-vy="add-credit-drawer-close">
              <CloseIcon />
            </IconButton>
          </Stack>

          <Divider />

          <Box sx={{ flexGrow: 1, padding: 2 }}>
            <AddCreditDrawerContent />
          </Box>

          <StickyToolbar>
            <Button
              variant="contained"
              color="primary"
              size="large"
              disabled={Object.keys(formMethods.formState.errors).length > 0}
              onClick={formMethods.handleSubmit((data) => {
                onAddCredit?.(data);
                onClose();
              })}
              data-vy="add-credit-drawer-submit"
              sx={{ backgroundColor: '#0D0D48' }}
            >
              {submitLabel}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              onClick={onClose}
              data-vy="add-credit-drawer-cancel"
            >
              Cancel
            </Button>
          </StickyToolbar>
        </Paper>
      </FormProvider>
    </Drawer>
  );
};
